import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import axios from 'axios'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface ReplyAttachment {
  id: number
  file_name: string
  download_url: string
}

/** Recipient entry as sent on the wire (matches to_emails shape). */
export interface ReplyRecipient {
  name: string
  email_address: string
}

/** Company-scoped reply label (FIR-7631). `color` is a LABEL_PALETTE key —
 * see features/campaigns/InboxLabels.tsx, kept in sync with the backend
 * ReplyLabelColor choices. */
export interface ApiReplyLabel {
  id: number
  name: string
  color: string
}

export interface Reply {
  id: number
  campaign_id: number | null
  folder: string
  subject: string | null
  html_body: string | null
  text_body: string | null
  is_read: boolean
  is_interested: boolean | null
  is_automated_reply: boolean
  is_tracked_reply: boolean
  date_received: string
  from_name: string | null
  from_email_address: string
  primary_to_email_address: string
  created_at: string
  updated_at: string
  // Flattened lead info
  lead_id: number | null
  lead_email: string | null
  lead_full_name: string | null
  lead_company: string | null
  // Annotated
  emails_sent_count: number | null
  thread_count: number
  thread_id: number | null
  is_flagged: boolean
  // Thread rows only (reply-threads endpoint): true when every visible
  // reply in the thread is automated (out-of-office auto-responder).
  // Drives the "Auto-reply" badge on the inbox list. Absent on rows from
  // the per-reply endpoints, where is_automated_reply is the signal.
  is_automated_only?: boolean
  // Thread rows only: custom labels applied to the thread (FIR-7631).
  labels?: ApiReplyLabel[]
  // Flattened sender email info
  sender_email_id: number | null
  sender_email_address: string | null
  sender_email_name: string | null
  // Nested
  attachments: ReplyAttachment[]
  // Recipients (normalized to wire shape by the backend serializer)
  to_emails: ReplyRecipient[]
  cc_emails: ReplyRecipient[]
  bcc_emails: ReplyRecipient[]
  // Take-over state. `taken_over_thread_id` is the local EmailThread the
  // conversation got handed off into; null means the thread is still in
  // the campaign-replies inbox and the operator can take it over.
  // `taken_over_by_email` is the integration mailbox (not the operator's
  // auth email); `taken_over_by_name` is the operator's display name.
  taken_over_thread_id: number | null
  taken_over_by_email: string | null
  taken_over_by_name: string | null
  taken_over_at: string | null
  // Inline "Different Address" affordance. Both null when there is no
  // resolvable Contact for this reply (no lead, or no Contact pointed at it
  // via Contact.bison_lead). Backend computes these from prefetched data
  // on the list queryset so the badge renders without an extra roundtrip.
  contact_id: number | null
  from_matches_contact: boolean | null
  // Client-only: an optimistically-appended send not yet confirmed by the
  // server. Rendered pending; on a 504 timeout it stays until the next refresh.
  __optimistic?: boolean
}

export interface RepliesResponse {
  count: number
  results: Reply[]
}

export interface SyncRepliesResponse {
  synced: number
  created: number
  updated: number
  errors: number
}

export interface ReplyFilters {
  thread_id?: number
  lead_id?: number
  campaign_id?: number
  campaign?: number[]
  search?: string
  sender_email?: string[]
  is_interested?: boolean
  is_read?: boolean
  is_flagged?: boolean
  /** Any-of filter by reply-label id (repeatable param, FIR-7631). */
  labels?: number[]
  page?: number
  page_size?: number
}

export interface ReplySummary {
  is_available: boolean
  total: number
  interested: number
  not_interested: number
  flagged: number
}

export interface ReplyToReplyPayload {
  message: string
  cc_emails?: ReplyRecipient[]
  bcc_emails?: ReplyRecipient[]
}

export interface ForwardReplyPayload {
  message: string
  to_emails: ReplyRecipient[]
  cc_emails?: ReplyRecipient[]
  bcc_emails?: ReplyRecipient[]
}

export interface TakeOverPayload {
  integration_id: number
  message: string
  cc_emails?: ReplyRecipient[]
  bcc_emails?: ReplyRecipient[]
  attachments?: File[]
}

export interface TakeOverResponse {
  status: 'taken_over'
  thread_id: number
  taken_over_thread_id: number | null
  taken_over_by_email: string | null
  taken_over_by_name: string | null
  taken_over_at: string | null
  reply_to_message_id: string
}

/**
 * Wire shape returned by the company-page-link helper. Mirrors the backend's
 * `ContactCompanyForLinkSerializer` — the canonical "navigate to this
 * company" payload used by `getCompanyV2Url`.
 */
export interface ContactCompanyForLink {
  id: number
  company_type: string | null
  chain_id: number | null
  university_id: number | null
  hospital_id: number | null
  nursinghome_id: number | null
  k12district_id: number | null
  lodging_id: number | null
  cstore_id: number | null
}

export interface ReplyInboxLink {
  email_thread_id: number
  contact_company: ContactCompanyForLink
}

// Query keys
export const replyKeys = {
  all: ['replies'] as const,
  list: (filters: ReplyFilters) => [...replyKeys.all, 'list', filters] as const,
  summary: (filters: ReplyFilters) =>
    [...replyKeys.all, 'summary', filters] as const,
}

export const replyThreadKeys = {
  all: ['reply-threads'] as const,
  list: (filters: ReplyFilters) =>
    [...replyThreadKeys.all, 'list', filters] as const,
  summary: (filters: ReplyFilters) =>
    [...replyThreadKeys.all, 'summary', filters] as const,
}

/** Shared by the inbox Add-label menu and the Settings "Reply Labels" tab so
 * both surfaces invalidate together (FIR-7631). */
export const replyLabelKeys = {
  all: ['reply-labels'] as const,
}

// DRF reads the repeatable filters (campaign, sender_email, labels) via
// request.GET.getlist(), which needs repeated keys (?labels=1&labels=2). The
// global axios paramsSerializer is qs.stringify with the default INDEXED
// array format (labels[0]=1) which getlist does NOT read — so the reply
// endpoints override per-request instead of changing the global serializer
// for every endpoint.
const repeatArrayParams = {
  paramsSerializer: (params: Record<string, unknown>) => {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue
      if (Array.isArray(value)) {
        value.forEach((v) => search.append(key, String(v)))
      } else {
        search.append(key, String(value))
      }
    }
    return search.toString()
  },
}

/** Invalidate both reply and reply-thread caches. */
function invalidateAllReplyCaches(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: replyKeys.all })
  queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
}

/** Snapshot of all reply/thread caches for rollback on optimistic update failure. */
type ReplySnapshot = [readonly unknown[], unknown][]

/**
 * Optimistically update a single reply across all cache locations
 * (non-paginated lists, thread detail, and infinite query pages).
 * Returns a snapshot of previous data for rollback via restoreReplySnapshot.
 */
function optimisticUpdateReply(
  queryClient: ReturnType<typeof useQueryClient>,
  updateResults: (results: Reply[]) => Reply[]
): ReplySnapshot {
  const snapshot: ReplySnapshot = [
    ...queryClient.getQueriesData<RepliesResponse>({ queryKey: replyKeys.all }),
    ...queryClient.getQueriesData<unknown>({ queryKey: replyThreadKeys.all }),
  ]

  // Non-paginated reply lists + thread detail
  for (const prefix of [
    [...replyKeys.all, 'list'],
    [...replyKeys.all, 'thread'],
  ]) {
    queryClient.setQueriesData<RepliesResponse>({ queryKey: prefix }, (old) => {
      if (!old) return old
      return { ...old, results: updateResults(old.results) }
    })
  }

  // Infinite query pages (replies + threads)
  for (const key of [
    [...replyKeys.all, 'infinite'],
    [...replyThreadKeys.all, 'infinite'],
  ]) {
    queryClient.setQueriesData<InfiniteData<RepliesResponse>>(
      { queryKey: key },
      (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            results: updateResults(page.results),
          })),
        }
      }
    )
  }

  return snapshot
}

/** Restore all reply/thread caches from a snapshot taken before an optimistic update. */
function restoreReplySnapshot(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ReplySnapshot
) {
  for (const [queryKey, data] of snapshot) {
    queryClient.setQueryData(queryKey, data)
  }
}

/**
 * Append a just-sent reply to the open thread-detail cache (keyed by
 * lead+campaign, see useGetThreadReplies) so it shows instantly. Scoped to the
 * one thread: the broad optimisticUpdateReply would also add a phantom inbox
 * row. Returns a snapshot for rollback.
 */
function appendOptimisticThreadReply(
  queryClient: ReturnType<typeof useQueryClient>,
  reply: Reply
): ReplySnapshot {
  const key = [
    ...replyKeys.all,
    'thread',
    reply.lead_id,
    reply.campaign_id,
  ] as const
  const snapshot: ReplySnapshot = [[key, queryClient.getQueryData(key)]]
  queryClient.setQueryData<RepliesResponse>(key, (old) =>
    old
      ? { ...old, results: [...old.results, reply] }
      : { count: 1, results: [reply] }
  )
  return snapshot
}

/** True when an error is an EmailBison send timeout surfaced as HTTP 504. */
export function isSendTimeoutError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 504
}

// API calls
const getReplies = async (filters: ReplyFilters): Promise<RepliesResponse> => {
  return baseAPICall<RepliesResponse>(() =>
    clientInstance.get('/emailbison/replies/', {
      params: filters,
      ...repeatArrayParams,
    })
  )
}

const syncCampaignReplies = async (
  bisonCampaignId: number
): Promise<SyncRepliesResponse> => {
  return baseAPICall<SyncRepliesResponse>(() =>
    clientInstance.post(`/emailbison/replies/sync/`, null, {
      params: { campaign_id: bisonCampaignId },
    })
  )
}

const getReplySummary = async (
  filters: ReplyFilters
): Promise<ReplySummary> => {
  return baseAPICall<ReplySummary>(() =>
    clientInstance.get('/emailbison/replies/summary/', {
      params: filters,
      ...repeatArrayParams,
    })
  )
}

const getReplyThreads = async (
  filters: ReplyFilters
): Promise<RepliesResponse> => {
  return baseAPICall<RepliesResponse>(() =>
    clientInstance.get('/emailbison/reply-threads/', {
      params: filters,
      ...repeatArrayParams,
    })
  )
}

const getReplyThreadSummary = async (
  filters: ReplyFilters
): Promise<ReplySummary> => {
  return baseAPICall<ReplySummary>(() =>
    clientInstance.get('/emailbison/reply-threads/summary/', {
      params: filters,
      ...repeatArrayParams,
    })
  )
}

const replyToReply = async (
  replyId: number,
  payload: ReplyToReplyPayload
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.post(`/emailbison/replies/${replyId}/reply/`, payload)
  )
}

const forwardReply = async (
  replyId: number,
  payload: ForwardReplyPayload
): Promise<void> => {
  return baseAPICall<void>(() =>
    clientInstance.post(`/emailbison/replies/${replyId}/forward/`, payload)
  )
}

const takeOver = async (
  threadId: number,
  payload: TakeOverPayload
): Promise<TakeOverResponse> => {
  // Sent as multipart/form-data so file attachments can ride along. The
  // recipient lists are JSON-stringified (the endpoint decodes them); axios
  // sets the multipart Content-Type + boundary automatically for FormData.
  const formData = new FormData()
  formData.append('integration_id', String(payload.integration_id))
  formData.append('message', payload.message)
  if (payload.cc_emails?.length) {
    formData.append('cc_emails', JSON.stringify(payload.cc_emails))
  }
  if (payload.bcc_emails?.length) {
    formData.append('bcc_emails', JSON.stringify(payload.bcc_emails))
  }
  payload.attachments?.forEach((file) => formData.append('attachments', file))

  return baseAPICall<TakeOverResponse>(() =>
    clientInstance.post(
      `/emailbison/reply-threads/${threadId}/take-over/`,
      formData
    )
  )
}

export const getReplyThreadInboxLink = async (
  threadId: number
): Promise<ReplyInboxLink> => {
  // 404s here mean "this thread is not taken over (yet)". Callers handle
  // them with their own messaging instead of the generic toast.
  return baseAPICall<ReplyInboxLink>(
    () =>
      clientInstance.get(`/emailbison/reply-threads/${threadId}/inbox-link/`),
    { silentStatuses: [404] }
  )
}

/**
 * Header strip on the reply view (FIR-7419): name + domain links + door
 * count. Reuses `ContactCompanyForLink` so `getCompanyV2Url` accepts it
 * directly when building the company-page link.
 */
export interface ThreadContactCompanyInfo extends ContactCompanyForLink {
  name: string | null
  domain: string | null
  door_count: number
}

export interface ThreadContactCompany {
  contact_company_id: number
  contact_id: number
  company_type_slug: string | null
  company: ThreadContactCompanyInfo | null
  // CRM contacts.Campaign id mapped from the thread's BisonCampaign (FIR-7420),
  // used to prefill the Create-Deal modal's origin_campaign. Null when no CRM
  // campaign is linked.
  campaign_id: number | null
  // The deal already linked to this thread (FIR-7420), or null. At most one
  // per thread; the strip renders it as a "Linked to <deal>" badge.
  deal: { id: number; name: string } | null
  // Product id of the thread's campaign (FIR-7420), used to preselect that
  // product row in the Create-Deal modal. Null when the campaign has no product.
  product_id: number | null
}

export const getThreadContactCompany = async (
  threadId: number
): Promise<ThreadContactCompany> => {
  // 404s here mean "sender not yet linked to a CRM contact" — callers show a
  // specific friendly message, so skip the generic "Request failed" toast.
  return baseAPICall<ThreadContactCompany>(
    () =>
      clientInstance.get(
        `/emailbison/reply-threads/${threadId}/contact-company/`
      ),
    { silentStatuses: [404] }
  )
}

/**
 * Lazy-fetch the contact-company info strip rendered on the right pane of
 * the campaign-replies inbox (FIR-7419). Thread-keyed (FIR follow-up): the
 * enrichment is thread-scoped, so keying on the stable `thread_id` avoids the
 * cache churn that a reply-id key suffered on every new reply. Swallows 404s
 * as `null` so the strip simply hides when there is no resolved CRM contact.
 */
export const useThreadContactCompany = (
  threadId: number | null | undefined
) => {
  return useQuery({
    queryKey: [...replyThreadKeys.all, 'contact-company', threadId] as const,
    queryFn: async (): Promise<ThreadContactCompany | null> => {
      if (threadId == null) return null
      try {
        return await getThreadContactCompany(threadId)
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return null
        }
        throw e
      }
    },
    enabled: threadId != null,
    staleTime: 60_000,
  })
}

export type ReplyContactEmailCategory = 'work' | 'personal' | 'generic'

export interface UpdateContactFromReplyPayload {
  email: string
  category: ReplyContactEmailCategory
  first_name?: string
  last_name?: string
  title?: string
}

export const useUpdateContactFromReply = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      contactId,
      payload,
    }: {
      contactId: number
      payload: UpdateContactFromReplyPayload
    }) => {
      // Map UI category → serializer field. Backend exposes `email_work`,
      // `email_personal`, `email_generic`; the UI calls the generic slot
      // "Other" but the wire name stays as `generic`.
      const body: Record<string, string> = {
        [`email_${payload.category}`]: payload.email,
      }
      if (payload.first_name !== undefined) body.first_name = payload.first_name
      if (payload.last_name !== undefined) body.last_name = payload.last_name
      if (payload.title !== undefined) body.title = payload.title
      // Suppress baseAPICall's own error toasts — MismatchEmailModal's
      // onError owns the messaging (specific "already in use" vs generic),
      // so we don't want a duplicate generic toast here.
      return baseAPICall<unknown>(
        () => clientInstance.patch(`/contacts/update/${contactId}/`, body),
        { disableNotifyError: true, disableNotifyGenericError: true }
      )
    },
    onSuccess: (_data, { contactId }) => {
      invalidateAllReplyCaches(queryClient)
      queryClient.invalidateQueries({ queryKey: ['contact', contactId] })
    },
  })
}

const markReplyRead = async (
  replyId: number,
  isRead: boolean
): Promise<Reply> => {
  return baseAPICall<Reply>(() =>
    clientInstance.patch(`/emailbison/replies/${replyId}/`, {
      is_read: isRead,
    })
  )
}

// Hooks
const REPLY_PAGE_SIZE = 25

export const useGetReplies = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyKeys.list(filters),
    queryFn: () => getReplies(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useGetRepliesInfinite = (filters: ReplyFilters = {}) => {
  return useInfiniteQuery({
    queryKey: [...replyKeys.all, 'infinite', filters],
    queryFn: ({ pageParam }) =>
      getReplies({ ...filters, page: pageParam, page_size: REPLY_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
  })
}

export const useGetReplyThreadsInfinite = (filters: ReplyFilters = {}) => {
  return useInfiniteQuery({
    queryKey: [...replyThreadKeys.all, 'infinite', filters],
    queryFn: ({ pageParam }) =>
      getReplyThreads({
        ...filters,
        page: pageParam,
        page_size: REPLY_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
  })
}

export const useGetReplyThreadSummary = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyThreadKeys.summary(filters),
    queryFn: () => getReplyThreadSummary(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useGetThreadReplies = (
  leadId: number | null,
  campaignId: number | null
) => {
  return useQuery({
    queryKey: [...replyKeys.all, 'thread', leadId, campaignId],
    queryFn: () => getReplies({ lead_id: leadId!, campaign_id: campaignId! }),
    enabled: !!leadId && !!campaignId,
  })
}

export const useMarkReplyInterested = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      isInterested,
    }: {
      replyId: number
      // null clears the verdict back to unclassified (FIR-7589)
      isInterested: boolean | null
    }) =>
      baseAPICall<Reply>(() =>
        clientInstance.post(`/emailbison/replies/${replyId}/mark-interested/`, {
          is_interested: isInterested,
        })
      ),

    onMutate: ({ replyId, isInterested }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) =>
          r.id === replyId ? { ...r, is_interested: isInterested } : r
        )
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useMarkReplyAutomated = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      isAutomated,
    }: {
      replyId: number
      isAutomated: boolean
    }) =>
      baseAPICall<Reply>(() =>
        clientInstance.patch(`/emailbison/replies/${replyId}/`, {
          is_automated_reply: isAutomated,
        })
      ),

    onMutate: ({ replyId, isAutomated }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) =>
          r.id === replyId ? { ...r, is_automated_reply: isAutomated } : r
        )
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useUnsubscribeReplyContact = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: number) =>
      baseAPICall<{ success: boolean }>(() =>
        clientInstance.post(`/emailbison/replies/${replyId}/unsubscribe/`)
      ),

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useDeleteReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (replyId: number) =>
      baseAPICall<{ success: boolean }>(() =>
        clientInstance.delete(`/emailbison/replies/${replyId}/delete/`)
      ),

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useToggleThreadFlag = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (threadId: number) =>
      baseAPICall<{ is_flagged: boolean }>(() =>
        clientInstance.post(
          `/emailbison/reply-threads/${threadId}/toggle-flag/`
        )
      ),

    onMutate: (threadId) => {
      const snapshot = queryClient.getQueriesData({
        queryKey: replyThreadKeys.all,
      })
      queryClient.setQueriesData<InfiniteData<RepliesResponse>>(
        { queryKey: [...replyThreadKeys.all, 'infinite'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((r) =>
                r.thread_id === threadId
                  ? { ...r, is_flagged: !r.is_flagged }
                  : r
              ),
            })),
          }
        }
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
    },
  })
}

// ---------------------------------------------------------------------------
// Reply labels (FIR-7631)
// ---------------------------------------------------------------------------

const getReplyLabels = async (): Promise<ApiReplyLabel[]> => {
  return baseAPICall<ApiReplyLabel[]>(() =>
    clientInstance.get('/emailbison/reply-labels/')
  )
}

export const useGetReplyLabels = () => {
  return useQuery({
    queryKey: replyLabelKeys.all,
    queryFn: getReplyLabels,
  })
}

export const useCreateReplyLabel = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { name: string; color: string }) =>
      baseAPICall<ApiReplyLabel>(() =>
        clientInstance.post('/emailbison/reply-labels/', payload)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: replyLabelKeys.all })
    },
  })
}

export const useUpdateReplyLabel = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: number
      name?: string
      color?: string
    }) =>
      baseAPICall<ApiReplyLabel>(() =>
        clientInstance.patch(`/emailbison/reply-labels/${id}/`, payload)
      ),
    onSuccess: () => {
      // Rename/recolor changes the pills on thread rows too.
      queryClient.invalidateQueries({ queryKey: replyLabelKeys.all })
      queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
    },
  })
}

export const useDeleteReplyLabel = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      baseAPICall<{ id: number; deleted: boolean }>(() =>
        clientInstance.delete(`/emailbison/reply-labels/${id}/`)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: replyLabelKeys.all })
      queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
    },
  })
}

/**
 * Pure cache-patch helper for the optimistic label toggle: adds the label to
 * the thread's row when absent, removes it when present. Exported for tests.
 */
export function toggleLabelInPages(
  data: InfiniteData<RepliesResponse> | undefined,
  threadId: number,
  label: ApiReplyLabel
): InfiniteData<RepliesResponse> | undefined {
  if (!data) return data
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      results: page.results.map((r) => {
        if (r.thread_id !== threadId) return r
        const current = r.labels ?? []
        const has = current.some((l) => l.id === label.id)
        return {
          ...r,
          labels: has
            ? current.filter((l) => l.id !== label.id)
            : [...current, label],
        }
      }),
    })),
  }
}

export const useToggleThreadLabel = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      threadId,
      label,
    }: {
      threadId: number
      label: ApiReplyLabel
    }) =>
      baseAPICall<{ label_id: number; applied: boolean }>(() =>
        clientInstance.post(
          `/emailbison/reply-threads/${threadId}/toggle-label/`,
          {
            label_id: label.id,
          }
        )
      ),

    onMutate: ({ threadId, label }) => {
      const snapshot = queryClient.getQueriesData({
        queryKey: replyThreadKeys.all,
      })
      queryClient.setQueriesData<InfiniteData<RepliesResponse>>(
        { queryKey: [...replyThreadKeys.all, 'infinite'] },
        (old) => toggleLabelInPages(old, threadId, label)
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) {
        for (const [key, data] of context.snapshot) {
          queryClient.setQueryData(key, data)
        }
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: replyThreadKeys.all })
    },
  })
}

export const useGetReplySummary = (filters: ReplyFilters = {}) => {
  return useQuery({
    queryKey: replyKeys.summary(filters),
    queryFn: () => getReplySummary(filters),
    enabled: filters.campaign_id === undefined || filters.campaign_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const useSyncCampaignReplies = (bisonCampaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCampaignReplies(bisonCampaignId),
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useReplyToReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      payload,
    }: {
      replyId: number
      payload: ReplyToReplyPayload
      // Optional: panel-built synthetic reply for the instant optimistic
      // append. Absent for callers that don't want optimism.
      optimisticReply?: Reply
    }) => replyToReply(replyId, payload),
    onMutate: ({ optimisticReply }) =>
      optimisticReply
        ? {
            snapshot: appendOptimisticThreadReply(queryClient, optimisticReply),
          }
        : undefined,
    onError: (error, _variables, context) => {
      // A 504 send timeout is not a confirmed failure: keep the optimistic
      // message in place and let the "refresh to confirm" notice stand.
      // Any other failure rolls the optimistic append back.
      if (context?.snapshot && !isSendTimeoutError(error)) {
        restoreReplySnapshot(queryClient, context.snapshot)
      }
    },
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useForwardReply = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      replyId,
      payload,
    }: {
      replyId: number
      payload: ForwardReplyPayload
      optimisticReply?: Reply
    }) => forwardReply(replyId, payload),
    onMutate: ({ optimisticReply }) =>
      optimisticReply
        ? {
            snapshot: appendOptimisticThreadReply(queryClient, optimisticReply),
          }
        : undefined,
    onError: (error, _variables, context) => {
      if (context?.snapshot && !isSendTimeoutError(error)) {
        restoreReplySnapshot(queryClient, context.snapshot)
      }
    },
    onSuccess: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useTakeOver = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      threadId,
      payload,
    }: {
      threadId: number
      payload: TakeOverPayload
    }) => takeOver(threadId, payload),
    onSuccess: (data, { threadId }) => {
      // Stamp the take-over fields on every cached copy of this thread so
      // the row pivots to the pill state instantly, without waiting on the
      // refetch that the cache invalidation triggers.
      optimisticUpdateReply(queryClient, (results) =>
        results.map((r) =>
          r.thread_id === threadId
            ? {
                ...r,
                taken_over_thread_id: data.taken_over_thread_id,
                taken_over_by_email: data.taken_over_by_email,
                taken_over_by_name: data.taken_over_by_name,
                taken_over_at: data.taken_over_at,
              }
            : r
        )
      )
      invalidateAllReplyCaches(queryClient)
    },
  })
}

export const useMarkReplyRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ replyId, isRead }: { replyId: number; isRead: boolean }) =>
      markReplyRead(replyId, isRead),

    onMutate: ({ replyId, isRead }) => {
      const snapshot = optimisticUpdateReply(queryClient, (results) =>
        results.map((r) => (r.id === replyId ? { ...r, is_read: isRead } : r))
      )
      return { snapshot }
    },

    onError: (_err, _vars, context) => {
      if (context?.snapshot) restoreReplySnapshot(queryClient, context.snapshot)
    },

    onSettled: () => {
      invalidateAllReplyCaches(queryClient)
    },
  })
}
